import { GameType } from '@modules/games/Game/constants/availableGames';

import { multiplyBlocksByLinesQnt } from '@modules/games/PlinkoGame/components/MuliplierBoxes/multipliers';
import { cardsArray } from '@legacyApp/client/modules/app/cardsService';

export const IMPLEMENTATION_CONFIG: Partial<
	Record<
		GameType,
		{
			code: string;
			description: Array<string>;
			implementation: string;
		}
	>
> = {
	[GameType.DICE]: {
		code: '5yrj0Lsw',
		description: [
			'To create a roll number, Wolfbet uses a multi-step process to create a roll number 0-99.99. Both client and server seeds and a nonce are combined with hmac-sha256(serverSeed, clientSeed_nonce) which will generate a hex string.',
			'The nonce is the # of rolls you made with the current seed pair. First five characters are taken from the hex string to create a roll number that is 0-1,048,575. If the roll number is over 999,999, the process is repeated with the next five characters skipping the previous set. This is done until a number less than 1,000,000 is achieved.',
			'In the astronomically unlikely event that all possible 5 character combinations are greater, 99.99 is used as the roll number. The resulting number 0-999,999 is applied a modulus of 10^4, to obtain a roll number 0-9999, and divided by 10^2 to result in a 0-99.99 number.',
		],
		implementation: `
        const verify = (server_seed, client_seed, nonce) => {
          const hash = CryptoJS.HmacSHA256('
            server_seed,
            \`\${client_seed}_\${nonce}\`,
          ).toString();
          let index = 0;
          let lucky = parseInt(hash.substr(index, 5), 16);
          while (lucky >= 1000000) {
            lucky = parseInt(hash.substr(index, 5), 16);
            index += 5;
          }
          return (lucky % 10000 / 100).toFixed(2);
        }
      `,
	},

	[GameType.HILO]: {
		code: 'apmw5qkL',
		description: [
			'To generate series of cards, Wolfbet uses a multi-step process to draw randomized cards stream. Both client and server seeds with a nonce and subnonce are combined with hmac-sha256(serverSeed, clientSeed_nonce_subNonce) which will generate a hex string.',
			'The nonce is the # of every bet you made with the current seed pair. Subnocne is number which represents the position of the card in the stream. First eight characters are taken from the hex string to create a sum number that is 0-1. Then sum is multiplied by number of cards in tile, which is 52. Result rounded with floor function indicates card position from cards array.',
		],
		implementation: `
        const cards = [${cardsArray.map((el) => `'${el}'`).join(',')}];
        const verify = (server_seed, client_seed, nonce, sub_nonce) => {
          const betHash = CryptoJS.HmacSHA256(
            server_seed,
            \`\${client_seed}_\${nonce}_\${sub_nonce}\`,
          ).toString();
          const floats = new Array(4).fill(0).map((el, index) => {
            return parseInt(betHash.substr(index * 2, 2), 16);
          });
          let sum = 0;
          floats.forEach((float, index) => {
            sum += round((float / Math.pow(256, index + 1)), 10);
          });
          return cards[round(sum * 52, 0, 'floor')];
        }
      `,
	},

	[GameType.LIMBO]: {
		code: 'to75pgd9',
		description: [
			'To create a bet result, Wolfbet uses a multi-step process. Both client and server seeds and a nonce are combined with hmac-sha256(serverSeed, clientSeed_nonce_0) which will generate a hex string.',
			'The nonce is the # of every bet you made with the current seed pair. First eight characters are taken from the hex string to create a sum number that is 0-1. Then sum is multiplied by maximum possible multiplier to create float number which is normalized. Normalized float is multiplied by the house edge. Then, in order to generate a game event that has probability distribution, we divide the maximum possible multiplier by the result of the first step to create the crash point.',
		],
		implementation: `
        const verify = (server_seed, client_seed, nonce, sub_nonce) => {
          const betHash = CryptoJS.HmacSHA256(
            server_seed,
            \`\${client_seed}_\${nonce}_0,
          ).toString();
          const floats = new Array(4).fill(0).map((el, index) => {
            return parseInt(betHash.substr(index * 2, 2), 16);
          });
          let sum = 0;
          floats.forEach((float, index) => {
            sum += round((float / Math.pow(256, index + 1)), 10);
          });

          // Sum multiplied by the maximum possible multiplier
          const float = sum * 1e8;
          // Make sure float is not zero
          const normalizedFloat = Math.max(float, 0.01)
          const floatPoint = 1e8 / normalizedFloat * 0.99
          // Make sure floatPoint is not higher than required
          const clampedFloatPoint = Math.min(floatPoint, 9900000)
          // Crash point rounded down to required denominator
          const crashPoint = Math.floor(clampedFloatPoint * 100) / 100;
          // Consolidate all crash points below 1
          return Math.max(crashPoint, 1);
        }
      `,
	},

	[GameType.PLINKO]: {
		code: '3hrz4gam',
		description: [
			'To generate a plinko ball path, Wolfbet uses a multi-step process. Both client and server seeds with a nonce and subnonce are combined with hmac-sha256(serverSeed, clientSeed_nonce_subNonce) which will generate a hex string.',
			'The nonce is the # of every bet you made with the current seed pair. Subnocne is an index which represents the next row in plinko pyramid. First eight characters are taken from the hex string to create a sum number that is 0-1. Then sum is multiplied by number of possible directions which is 2. All row results rounded with floor function are summed and indicates payout index of box on which ball will land.',
		],
		implementation: `
		const multipliers = {
					${Object.keys(multiplyBlocksByLinesQnt)
						.map((risk) => {
							return `${risk}: {
							${Object.keys(multiplyBlocksByLinesQnt[risk]).map((lines) => {
								return `${lines}: [${multiplyBlocksByLinesQnt[risk][lines].join(
									`,`,
								)}], `;
							}).join(`
							`)}
					},
					`;
						})
						.join('')}
		}

				const verify = (server_seed, client_seed, nonce, sub_nonce) => {
          const betHash = CryptoJS.HmacSHA256(
            server_seed,
            \`\${client_seed}_\${nonce}_\${sub_nonce}\`,
          ).toString();
          const floats = new Array(4).fill(0).map((el, index) => {
            return parseInt(betHash.substr(index * 2, 2), 16);
          });
          let sum = 0;
          floats.forEach((float, index) => {
            sum += round((float / Math.pow(256, index + 1)), 10);
          });
          return round(sum * 2, 0, 'floor');
        }

        const generatePath = (server_seed, client_seed, nonce, rows, risk) => {
					const path = [];
					let outcome = rows;
					for (let i = 0; i < rows; i++) {
						path.push(verify(server_seed, client_seed, nonce, i));
					}
					const payoutIndex = path.reduce((acc, el) => acc + el, 0);
					return multipliers[risk][rows][payoutIndex];
				}
      `,
	},

	[GameType.KENO]: {
		code: 'hd456yzm',
		description: [
			'To generate a Keno result, Wolfbet uses a multi-step process. Both client and server seeds with a nonce and subnonce are combined with hmac-sha256(serverSeed, clientSeed_nonce_subNonce) which will generate a hex string.',
			'The nonce is the # of every bet you made with the current seed pair. Subnocne is an index which represents the tile in Keno board. First eight characters are taken from the hex string to create a sum number that is 0-1. Then sum is multiplied by number of possible directions which is 40. This sum represents result number position in array which on every subNonce is shiffled with fisher-yates implementation is utilised to prevent duplicates of possible hits being generated.',
		],
		implementation: `
      const RESULTS_COUNT = 40;
      const DRAW_COUNT = 10;
      const generateResult = (server_seed: string, client_seed: string, nonce: number, sub_nonce: number, resultsCount: number): number => {
        const betHash = CryptoJS.HmacSHA256(
          server_seed,
          \`\${client_seed}_\${nonce}\${sub_nonce},
                ).toString();
        const floats = new Array(4).fill(0).map((el, index) => {
          return parseInt(betHash.substr(index * 2, 2), 16);
        });
        let sum = 0;
        floats.forEach((float, index) => {
          sum += round((float / Math.pow(256, index + 1)), 10);
        });
        console.log({floats,sum});
        return round(sum * resultsCount, 0, 'floor');
      }

      const round = (oldAmount: number, decimal: number, direction: string = 'round'): number => {
          if (!oldAmount && oldAmount !== 0) return oldAmount;
          let amount = parseFloat(oldAmount);
          decimal = Math.pow(10, decimal);
          let result = Math[direction](amount * decimal) / decimal;
          return (result === 0) ? 0 : result;
      };

      const generateSquares = (length: number): Array<number> => {
        return new Array(length).fill(0).map((el, index) => index + 1);
      }

      const generateResults = (server_seed: string, client_seed: string, nonce: number, drawCount: number): Array<number> => {
        const results = [];
        const squareArray = generateSquares(RESULTS_COUNT);
        for (let i = 0; i < drawCount; i++) {
          console.log(squareArray.length, squareArray);
          const maxIndex = squareArray.length - 1;
          const hitIndex = generateResult(server_seed, client_seed, nonce, i, squareArray.length);
          const hit = squareArray[hitIndex];
          squareArray[hitIndex] = squareArray[maxIndex];
          squareArray.pop();
          results.push(hit);
        }
        console.log('generateResults', {server_seed, client_seed, nonce, results});
        return results;
      }
    `,
	},
};
